/**
 * Learning objectives in the ABCD format — Audience, Behavior, Condition, Degree — and
 * a check for verbs that cannot be observed.
 *
 * "Understand", "know", "learn" and "appreciate" describe something that happens inside
 * a learner's head; an objective built on them gives no way to tell whether it was met.
 * The check flags them (and their regular inflections) wherever they appear, so the
 * widget can suggest an observable verb from the right level of Bloom's revised
 * taxonomy (Anderson & Krathwohl, 2001) instead.
 */

export const BLOOM_LEVELS = [
  "remember",
  "understand",
  "apply",
  "analyze",
  "evaluate",
  "create",
] as const;

export type BloomLevel = (typeof BLOOM_LEVELS)[number];

export type ObjectiveOrder = "condition-first" | "audience-first";

export interface ObjectiveParts {
  audience: string;
  verb: string;
  content: string;
  condition: string;
  degree: string;
}

const SPACES = /\s+/g;
const TRAILING_PUNCTUATION = /[\s.,;:!?]+$/;
const WORD_SPLIT = /[^\p{L}\p{M}'’-]+/u;

function clean(part: string): string {
  return part.replace(SPACES, " ").replace(TRAILING_PUNCTUATION, "").trim();
}

function capitalizeFirst(text: string, locale?: string): string {
  return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
}

/**
 * One objective sentence.
 *
 * Condition first: "Given a labeled diagram, students will be able to explain the
 * stages of mitosis with at least 80% accuracy." Audience first moves the condition
 * after the behavior. `will` is the phrase between audience and verb ("will" or
 * "will be able to").
 */
export function buildObjective(
  parts: ObjectiveParts,
  order: ObjectiveOrder,
  will: string,
  locale?: string
): string {
  const audience = clean(parts.audience);
  const behavior = [clean(parts.verb), clean(parts.content)]
    .filter((part) => part.length > 0)
    .join(" ");
  const condition = clean(parts.condition);
  const degree = clean(parts.degree);
  if (behavior.length === 0) {
    return "";
  }
  const sentence =
    order === "condition-first" && condition.length > 0
      ? `${condition}, ${[audience, will, behavior, degree].filter((part) => part.length > 0).join(" ")}`
      : [audience, will, behavior, condition, degree]
          .filter((part) => part.length > 0)
          .join(" ");
  return `${capitalizeFirst(sentence, locale)}.`;
}

function inflections(word: string): string[] {
  const forms = [word, `${word}s`, `${word}es`, `${word}ed`, `${word}ing`];
  if (word.endsWith("e")) {
    forms.push(`${word}d`, `${word.slice(0, -1)}ing`);
  }
  return forms;
}

function matchesAt(
  words: readonly string[],
  start: number,
  phrase: readonly string[]
): boolean {
  const [head = "", ...tail] = phrase;
  if (!inflections(head).includes(words[start] ?? "")) {
    return false;
  }
  return tail.every((part, offset) => words[start + 1 + offset] === part);
}

/**
 * The vague verbs (or phrases, like "be aware of") that occur in a text, in the order
 * they are listed. Case-insensitive, whole words, regular inflections included —
 * "understands" and "learned" are caught; irregular forms such as "knew" are not.
 */
export function findVagueTerms(
  text: string,
  vague: readonly string[],
  locale?: string
): string[] {
  const words = text
    .toLocaleLowerCase(locale)
    .split(WORD_SPLIT)
    .filter((word) => word.length > 0);
  return vague.filter((term) => {
    const phrase = term.toLocaleLowerCase(locale).split(" ");
    return words.some((_, index) => matchesAt(words, index, phrase));
  });
}
